import React, { FC } from 'react';
import moment from 'moment';
import classnames from 'classnames';
import pluralize from 'pluralize';
import { Markdown } from '@src/components/Markdown';
import { Avatar } from '@src/components/Avatar';
import { getNameInitials } from '@src/util/getNameInitials';
import { AvatarSize } from '@src/components/Avatar/enums/avatarSize.enum';
import { WorkHistoryItemType } from './interfaces/workHistoryItem.interface';
import { WorkHistoryProps } from './props';
import './styles.scss';

export const WorkHistoryItem: FC<WorkHistoryProps> = ({ item }) => {
  const getWorkPeriodLabel = (years: number, months: number): string => {
    let workPeriodLabel = '';

    if (years > 0) {
      workPeriodLabel += `${pluralize('year', years, true)}${' '}`;
    }

    if (months > 0) {
      workPeriodLabel += pluralize('month', months, true);
    }

    return workPeriodLabel.trim();
  };

  const getWorkTimeSpentLabel = (item: WorkHistoryItemType): string => {
    if (!item.startsAt?.day || !item.startsAt?.month || !item.startsAt?.year) {
      return '';
    }

    if (!item.endsAt?.day || !item.endsAt?.month || !item.endsAt?.year) {
      return `${moment(`${item.startsAt?.day}/${item.startsAt?.month}/${item.startsAt?.year}`).format(
        'MMM YYYY',
      )} - Present`;
    }

    const startDate = moment(`${item.startsAt?.day}/${item.startsAt?.month}/${item.startsAt?.year}`);
    const endDate = moment(`${item.endsAt?.year}-${item.endsAt?.month}-${item.endsAt?.day}`);
    const workPeriod = endDate.diff(startDate, 'months');
    const years = Math.floor(workPeriod / 12);
    const months = workPeriod - years * 12;
    const workPeriodLabel = getWorkPeriodLabel(years, months);

    return `${startDate.format('MMM YYYY')} - ${endDate.format('MMM YYYY')} • ${workPeriodLabel}`;
  };

  const companyLogo = item?.company?.profile?.logo?.fullUrl;

  return (
    <div className="work-history-item">
      <div className="work-history-item__header">
        {companyLogo || item.companyName ? (
          <Avatar
            className={classnames(
              'work-history-item__company-logo',
              !companyLogo && 'work-history-item__company-logo--without-image',
            )}
            initials={getNameInitials(item.companyName)}
            size={AvatarSize.LARGE}
            source={companyLogo}
          />
        ) : null}

        <div className="work-history-item__header-info">
          <div className="work-history-item__work-place">
            <span className="work-history-item__header-info-dark">{item.title}</span>
            {item.companyName ? (
              <>
                <span> @ </span>
                <span className="work-history-item__header-info-dark">{item.companyName}</span>
              </>
            ) : null}
          </div>
          <div className="work-history-item__work-period">{getWorkTimeSpentLabel(item)}</div>
        </div>
      </div>

      {item.description && <Markdown markdown={item.description} wrapperClassName="work-history-item__text" />}
    </div>
  );
};
